import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Mail, ArrowLeft } from "lucide-react";
import { useAuthMethods } from "../../backend/queries/auth";
import { authApi } from "../../backend/api/auth";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";
import { AuthLayout } from "./auth-layout";
import { GoogleLogo, MicrosoftLogo } from "./provider-logos";

export default function Invite(): JSX.Element {
  const [params] = useSearchParams();
  const methods = useAuthMethods();
  const providers = methods.data?.providers.filter(provider => provider.configured) || [];
  const emailAvailable = methods.data?.email_configured === true;
  const token = params.get("token");
  const [emailChosen, setEmailChosen] = useState(false);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const validLink = Boolean(token && /^[a-f0-9]{64}$/i.test(token));

  async function verify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!validLink || busy) return;
    setBusy(true);
    setError("");
    try {
      const result = await authApi.requestPasswordReset(email.trim(), token!);
      if (result.error) throw result.error;
      setSent(true);
    } catch {
      setError("We couldn't send the verification link. Please try again later.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
        <Card>
          <CardHeader className="space-y-1.5 pb-5 text-center">
            <CardTitle className="text-2xl font-bold">{sent ? "Check your inbox" : "Welcome to your portal"}</CardTitle>
            <CardDescription>{!validLink ? "Open the personal invitation link we sent you to get started." : emailChosen ? "Verify your invited email to create a portal password." : "Choose how you’d like to continue. Use the email address your invitation was sent to."}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            {validLink && !emailChosen && <>
              <Button asChild><Link to="/login">Sign in with temporary password</Link></Button>
              <p className="text-sm text-muted-foreground">Use the email and temporary password provided by the person who invited you. You’ll choose your own password after signing in. If you only received a link, ask them for new sign-in instructions.</p>
              {methods.isPending && <p role="status" className="text-sm text-muted-foreground">Loading sign-in options…</p>}
              {providers.map(({ provider }) => <Button key={provider} variant="outline" asChild><a href={authApi.providerSignInUrl(provider, undefined, token!)}>{provider === "google" ? <GoogleLogo className="mr-2 h-4 w-4 shrink-0" /> : <MicrosoftLogo className="mr-2 h-4 w-4 shrink-0" />}Continue with {provider === "google" ? "Google" : "Microsoft"}</a></Button>)}
              {providers.length > 0 && emailAvailable && <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-muted-foreground"><span className="flex-1 border-t" />or<span className="flex-1 border-t" /></div>}
              {emailAvailable && <Button onClick={() => setEmailChosen(true)}><Mail className="mr-2 h-4 w-4" />Continue with email</Button>}

            </>}
            {validLink && emailChosen && !sent && <form onSubmit={verify} className="grid gap-4" aria-busy={busy}>
              <div className="grid gap-2"><Label htmlFor="invite-email">Email address</Label><Input id="invite-email" type="email" autoComplete="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="name@example.com" /></div>
              {error && <Alert variant="destructive"><AlertTitle>Unable to send link</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}
              <Button type="submit" disabled={busy}>{busy ? "Sending..." : "Send verification link"}</Button>
            </form>}
            {sent && <p role="status" className="text-sm text-muted-foreground">If this email matches an active invitation, you’ll receive a verification link. Follow it to set your password. Check your spam folder too.</p>}
            {emailChosen && <Button variant="ghost" disabled={busy} onClick={() => { setEmailChosen(false); setSent(false); setError(""); }}><ArrowLeft className="mr-2 h-4 w-4" />Other sign-in options</Button>}
            <div className="border-t pt-4 text-center text-sm text-muted-foreground">Already set up? <Link className="font-medium text-foreground underline" to="/login">Sign in</Link></div>
          </CardContent>
        </Card>
    </AuthLayout>
  );
}
